package com.karyadina.service

import com.karyadina.model.*
import kotlinx.coroutines.*
import com.karyadina.util.currentTimeMillis

/**
 * Service for managing Pomodoro timer sessions
 */
interface PomodoroService {
    /**
     * Start a new pomodoro session
     */
    fun startSession(sessionType: SessionType, task: String? = null): PomodoroSession
    
    /**
     * Pause the current session
     */
    fun pauseSession(sessionId: String)
    
    /**
     * Resume a paused session
     */
    fun resumeSession(sessionId: String)
    
    /**
     * Complete the current session
     */
    fun completeSession(sessionId: String): PomodoroSession
    
    /**
     * Cancel/interrupt the current session
     */
    fun cancelSession(sessionId: String)
    
    /**
     * Get current session
     */
    fun getCurrentSession(): PomodoroSession?
    
    /**
     * Get session history
     */
    fun getSessionHistory(limit: Int = 10): List<PomodoroSession>
    
    /**
     * Get statistics
     */
    fun getStatistics(): PomodoroStats
    
    /**
     * Get configuration
     */
    fun getConfig(): PomodoroConfig
    
    /**
     * Update configuration
     */
    fun updateConfig(config: PomodoroConfig)
    
    /**
     * Get remaining time in seconds
     */
    fun getRemainingTime(sessionId: String): Int
    
    /**
     * Clear all session history
     */
    fun clearHistory()
    
    /**
     * Refresh data from storage (e.g. after cloud sync)
     */
    fun refreshFromStorage()
}

/**
 * Simple implementation of PomodoroService with persistent storage
 */
class PomodoroServiceImpl(
    private val storage: PomodoroStorage = PomodoroStorageImpl()
) : PomodoroService {
    private var currentSession: PomodoroSession? = null
    private var config: PomodoroConfig
    private val sessionHistory: MutableList<PomodoroSession>
    private var pausedAt: Long? = null
    private var totalPausedTime: Long = 0
    
    init {
        // Load persisted data
        config = storage.loadConfig() ?: PomodoroConfig()
        sessionHistory = storage.loadSessions().toMutableList()
        
        // Listen for storage refresh signals
        kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Default).launch {
            AppStorage.refreshNotifier.collect {
                refreshFromStorage()
            }
        }
    }
    
    override fun startSession(sessionType: SessionType, task: String?): PomodoroSession {
        val duration = when (sessionType) {
            SessionType.FOCUS -> config.focusDuration
            SessionType.SHORT_BREAK -> config.shortBreakDuration
            SessionType.LONG_BREAK -> config.longBreakDuration
        }
        
        val session = PomodoroSession(
            id = generateId(),
            sessionType = sessionType.name,
            startTime = currentTimeMillis(),
            durationMinutes = duration,
            task = task
        )
        
        currentSession = session
        pausedAt = null
        totalPausedTime = 0
        
        return session
    }
    
    override fun pauseSession(sessionId: String) {
        if (currentSession?.id == sessionId && pausedAt == null) {
            pausedAt = currentTimeMillis()
        }
    }
    
    override fun resumeSession(sessionId: String) {
        pausedAt?.let { pauseTime ->
            totalPausedTime += currentTimeMillis() - pauseTime
            pausedAt = null
        }
    }
    
    override fun completeSession(sessionId: String): PomodoroSession {
        val session = currentSession
        if (session?.id == sessionId) {
            val completedSession = session.copy(
                endTime = currentTimeMillis(),
                completed = true,
                updatedAt = currentTimeMillis()
            )
            sessionHistory.add(0, completedSession)
            currentSession = null
            
            // Persist to storage
            storage.saveSessions(sessionHistory)
            storage.saveStats(getStatistics())
            
            return completedSession
        }
        
        // Defensive: check if session exists in history (handles stale ticker from old session)
        val existingSession = sessionHistory.find { it.id == sessionId }
        if (existingSession != null) {
            // Session is already in history, just return it gracefully
            // This prevents crashes when old ticker coroutines try to complete stale sessions
            return existingSession
        }
        
        // Last resort: create and return a dummy completed session to prevent crash
        // This can only happen if somehow we're trying to complete with an ID that doesn't exist
        val dummySession = PomodoroSession(
            id = sessionId,
            sessionType = SessionType.FOCUS.name,
            startTime = currentTimeMillis(),
            durationMinutes = 25,
            endTime = currentTimeMillis(),
            completed = true
        )
        return dummySession
    }
    
    override fun cancelSession(sessionId: String) {
        val session = currentSession
        if (session?.id == sessionId) {
            val cancelledSession = session.copy(
                endTime = currentTimeMillis(),
                interrupted = true,
                updatedAt = currentTimeMillis()
            )
            sessionHistory.add(0, cancelledSession)
            currentSession = null
            
            // Persist to storage
            storage.saveSessions(sessionHistory)
        }
    }
    
    override fun getCurrentSession(): PomodoroSession? = currentSession
    
    override fun getSessionHistory(limit: Int): List<PomodoroSession> {
        return sessionHistory.take(limit)
    }
    
    override fun getStatistics(): PomodoroStats {
        val completed = sessionHistory.filter { it.completed && it.sessionType == SessionType.FOCUS.name }
        val totalFocus = completed.sumOf { it.durationMinutes.toLong() }
        
        val now = currentTimeMillis()
        val todayStart = now - (now % (24 * 60 * 60 * 1000))
        val weekStart = todayStart - (6 * 24 * 60 * 60 * 1000)
        
        val todayFocus = completed
            .filter { it.startTime >= todayStart }
            .sumOf { it.durationMinutes.toLong() }
        
        val weekFocus = completed
            .filter { it.startTime >= weekStart }
            .sumOf { it.durationMinutes.toLong() }
        
        // Calculate streak
        val sortedSessions = completed.sortedByDescending { it.startTime }
        var currentStreak = 0
        var lastDay: Long? = null
        
        for (session in sortedSessions) {
            val sessionDay = session.startTime / (24 * 60 * 60 * 1000)
            if (lastDay == null || sessionDay == lastDay - 1) {
                currentStreak++
                lastDay = sessionDay
            } else if (sessionDay < lastDay!! - 1) {
                break
            }
        }
        
        return PomodoroStats(
            totalSessions = sessionHistory.size,
            completedSessions = completed.size,
            totalFocusTime = totalFocus,
            todayFocusTime = todayFocus,
            weeklyFocusTime = weekFocus,
            currentStreak = currentStreak,
            longestStreak = currentStreak // Simplified for now
        )
    }
    
    override fun getConfig(): PomodoroConfig = config
    
    override fun updateConfig(config: PomodoroConfig) {
        this.config = config
        // Persist config to storage
        storage.saveConfig(config)
    }
    
    override fun getRemainingTime(sessionId: String): Int {
        val session = currentSession ?: return 0
        if (session.id != sessionId) return 0
        
        val elapsed = if (pausedAt != null) {
            pausedAt!! - session.startTime - totalPausedTime
        } else {
            currentTimeMillis() - session.startTime - totalPausedTime
        }
        
        val remainingMs = (session.durationMinutes * 60 * 1000) - elapsed
        return (remainingMs / 1000).toInt().coerceAtLeast(0)
    }
    
    override fun clearHistory() {
        sessionHistory.clear()
        // Persist the empty history to storage
        storage.saveSessions(emptyList())
        storage.saveStats(getStatistics())
    }
    
    override fun refreshFromStorage() {
        config = storage.loadConfig() ?: PomodoroConfig()
        sessionHistory.clear()
        sessionHistory.addAll(storage.loadSessions())
    }

    private fun generateId(): String {
        return "session_${currentTimeMillis()}_${(1000..9999).random()}"
    }
}
